import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ChevronLeft, ShoppingCart, Minus, Plus, Check, Truck, Shield, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { useCart, useAuth } from "../App";
import { toast } from "sonner";
import { WishlistButton } from "../components/WishlistButton";
import { ReviewsSection } from "../components/ReviewsSection";
import { SocialShareButtons } from "../components/SocialShareButtons";
import LikeButton from "../components/LikeButton";
import { MerchRecommendations } from "../components/Recommendations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryNames = {
  hoodies: "Hoodies",
  shirts: "T-Shirts",
  pants: "Pants",
  jackets: "Jackets",
  phone_cases: "Phone Cases",
  stickers: "Stickers",
  cups: "Mugs & Drinkware",
  posters: "Posters & Wall Art",
  hats: "Hats & Headwear",
  tank_tops: "Tank Tops",
  joggers: "Joggers",
  tote_bags: "Tote Bags",
  laptop_sleeves: "Laptop Sleeves",
  backpacks: "Backpacks",
  socks: "Socks",
  mouse_pads: "Desk Pads",
  blankets: "Blankets",
  flags: "Flags & Banners",
};

const MerchDetailPage = () => {
  const { merchId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [abVariant, setAbVariant] = useState(null);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await axios.get(`${API}/merch/${merchId}`);
        setItem(response.data);
        
        // Fetch A/B test variant if any
        try {
          const abRes = await axios.get(`${API}/merch/${merchId}/ab-variant`);
          if (abRes.data.has_test) {
            setAbVariant(abRes.data);
          }
        } catch (e) { /* no test active */ }
        
        // Set default selections
        const variants = response.data.variants || [];
        const sizes = [...new Set(variants.map(v => v.size).filter(Boolean))];
        const colors = [...new Set(variants.map(v => v.color).filter(Boolean))];
        
        if (sizes.length > 0) setSelectedSize(sizes[0]);
        if (colors.length > 0) setSelectedColor(colors[0]);
      } catch (error) {
        toast.error("Item not found");
        navigate("/merch");
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [merchId, navigate]);

  const getAvailableSizes = () => {
    if (!item?.variants) return [];
    return [...new Set(item.variants.map(v => v.size).filter(Boolean))];
  };

  const getAvailableColors = () => {
    if (!item?.variants) return [];
    return [...new Set(item.variants.map(v => v.color).filter(Boolean))];
  };

  const getSelectedVariant = () => {
    if (!item?.variants) return null;
    return item.variants.find(v => 
      (!selectedSize || v.size === selectedSize) && 
      (!selectedColor || v.color === selectedColor)
    );
  };

  const isInStock = () => {
    const variant = getSelectedVariant();
    return variant ? variant.stock > 0 : true;
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      return;
    }

    if (!isInStock()) {
      toast.error("This variant is out of stock");
      return;
    }

    setAddingToCart(true);
    const success = await addToCart({
      item_type: "merch",
      item_id: item.merch_id,
      size: selectedSize || null,
      color: selectedColor || null,
      quantity
    });
    
    // Track A/B conversion on successful add to cart
    if (success && abVariant) {
      try {
        await axios.post(`${API}/merch/${item.merch_id}/ab-convert`, {
          test_id: abVariant.test_id,
          variant: abVariant.variant
        });
      } catch (e) { /* silent */ }
    }
    
    setAddingToCart(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-neon-magenta border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!item) return null;

  const sizes = getAvailableSizes();
  const colors = getAvailableColors();

  return (
    <div className="min-h-screen py-10 px-6" data-testid="merch-detail-page">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/merch")}
          className="mb-6 text-zinc-400 hover:text-white"
          data-testid="back-button"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Merch
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left Column - Images */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Main Image */}
              <div className="aspect-square bg-zinc-900 overflow-hidden mb-4">
                <img
                  src={item.images[currentImageIndex] || ""}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Thumbnail Gallery */}
              {item.images.length > 1 && (
                <div className="flex gap-2">
                  {item.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-20 h-20 overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex ? 'border-neon-magenta' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`${item.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column - Details */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Badge className="bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50 mb-4">
                {categoryNames[item.category] || item.category}
              </Badge>
              
              <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-4">
                {item.name}
              </h1>
              
              <div className="flex items-center gap-3 mb-4">
                <WishlistButton itemType="merch" itemId={item.merch_id} />
                <LikeButton itemType="merch" itemId={item.merch_id} />
              </div>

              <SocialShareButtons title={item.name} url={window.location.href} />
              
              <p className="text-zinc-400 mb-6">{item.description}</p>

              {/* Fulfillment Info */}
              {item.is_dropshipped && (
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-neon-cyan/10 border border-neon-cyan/20">
                    <Truck className="w-4 h-4 text-neon-cyan" />
                    <span className="text-xs text-neon-cyan">Ships Worldwide via Printify</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-green-500/10 border border-green-500/20">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-green-400">Quality Guaranteed</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-purple-500/10 border border-purple-500/20">
                    <Package className="w-4 h-4 text-purple-400" />
                    <span className="text-xs text-purple-400">Print on Demand</span>
                  </div>
                </div>
              )}

              {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-6">
                  {item.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="border-white/10 text-zinc-400 text-[10px]">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              <p className="font-heading text-4xl text-neon-magenta mb-8">
                ${item.price.toFixed(2)}
              </p>

              <div className="glass p-6 md:p-8 space-y-6">
                {/* Size Selection */}
                {sizes.length > 0 && (
                  <div>
                    <Label className="text-sm uppercase tracking-wide text-zinc-400 mb-3 block">
                      Size
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-2 border transition-all ${
                            selectedSize === size
                              ? 'border-neon-magenta bg-neon-magenta/20 text-white'
                              : 'border-white/20 hover:border-white/40'
                          }`}
                          data-testid={`size-${size}`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Color Selection */}
                {colors.length > 0 && (
                  <div>
                    <Label className="text-sm uppercase tracking-wide text-zinc-400 mb-3 block">
                      Color
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`px-4 py-2 border transition-all ${
                            selectedColor === color
                              ? 'border-neon-magenta bg-neon-magenta/20 text-white'
                              : 'border-white/20 hover:border-white/40'
                          }`}
                          data-testid={`color-${color}`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity */}
                <div>
                  <Label className="text-sm uppercase tracking-wide text-zinc-400 mb-3 block">
                    Quantity
                  </Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="border-white/20"
                      data-testid="decrease-quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="font-mono text-xl w-12 text-center">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(quantity + 1)}
                      className="border-white/20"
                      data-testid="increase-quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Stock Status */}
                <div className="flex items-center gap-2">
                  {isInStock() ? (
                    <>
                      <Check className="w-4 h-4 text-neon-green" />
                      <span className="text-sm text-neon-green">In Stock</span>
                    </>
                  ) : (
                    <span className="text-sm text-red-500">Out of Stock</span>
                  )}
                </div>

                {/* Add to Cart */}
                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-zinc-400">Total:</span>
                    <span className="font-heading text-3xl text-neon-magenta">
                      ${(item.price * quantity).toFixed(2)}
                    </span>
                  </div>
                  <Button
                    onClick={handleAddToCart}
                    disabled={addingToCart || !isInStock()}
                    className="w-full bg-neon-magenta text-black font-bold uppercase tracking-widest py-6 btn-clip neon-magenta hover:scale-[1.02] transition-transform"
                    data-testid="add-to-cart-button"
                  >
                    {addingToCart ? (
                      "Adding..."
                    ) : !isInStock() ? (
                      "Out of Stock"
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-10 max-w-6xl mx-auto">
          <ReviewsSection itemType="merch" itemId={item.merch_id} />
        </div>

        {/* Recommendations */}
        <div className="max-w-6xl mx-auto">
          <MerchRecommendations merchId={item.merch_id} />
        </div>
      </div>
    </div>
  );
};

export default MerchDetailPage;
